import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import DatePicker from '../../components/DatePicker/DatePicker';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/FormPage.css';

function formatMoney(value) {
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '—';
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getBillAmountIncGst(bill) {
    if (bill?.amountIncGst != null && bill.amountIncGst !== '') {
        return parseFloat(bill.amountIncGst);
    }
    const totalAmount = parseFloat(bill?.totalAmount || 0);
    const gstRate = parseFloat(bill?.gstRate || 0);
    const gstAmount = totalAmount * ((gstRate * 2) / 100);
    return parseFloat((totalAmount + gstAmount).toFixed(2));
}

function CreateIncome() {
    const { incomeId } = useParams();
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(Boolean(incomeId));
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [bills, setBills] = useState([]);
    const [showBillModal, setShowBillModal] = useState(false);
    const [billSearchTerm, setBillSearchTerm] = useState('');
    const [selectedBill, setSelectedBill] = useState(null);
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        amount: '',
        type: 'Cash',
        description: '',
        bindToBill: false,
        clientId: '',
        projectId: '',
        billId: ''
    });

    const fetchBills = useCallback(async (clientId, projectId) => {
        if (!clientId || !formData.bindToBill) {
            setBills([]);
            return;
        }
        try {
            const params = { clientId: Number(clientId) };
            if (projectId) {
                params.projectId = Number(projectId);
            }
            const response = await api.get('/bills', { params });
            setBills(response.data);
        } catch (error) {
            console.error('Error fetching bills:', error);
            setBills([]);
        }
    }, [formData.bindToBill]);

    useEffect(() => {
        withLoading(async () => {
            const clientsRes = await api.get('/clients');
            setClients(clientsRes.data);

            const projectsRes = await api.get('/projects');
            setProjects(projectsRes.data);

            if (incomeId) {
                const incomeRes = await api.get(`/income/${incomeId}`);
                const income = incomeRes.data;
                const bindToBill = Boolean(income.billId);
                setFormData({
                    date: income.date ? income.date.split('T')[0] : '',
                    amount: String(income.amount ?? ''),
                    type: income.type || 'Cash',
                    description: income.description || '',
                    bindToBill,
                    clientId: income.clientId ? String(income.clientId) : '',
                    projectId: income.projectId ? String(income.projectId) : '',
                    billId: income.billId ? String(income.billId) : ''
                });

                if (bindToBill && income.clientId) {
                    const params = { clientId: income.clientId };
                    if (income.projectId) {
                        params.projectId = income.projectId;
                    }
                    const billsRes = await api.get('/bills', { params });
                    setBills(billsRes.data);
                    const matchedBill = billsRes.data.find(
                        (bill) => String(bill.id) === String(income.billId)
                    );
                    if (matchedBill) {
                        setSelectedBill(matchedBill);
                    } else if (income.invoiceNo) {
                        setSelectedBill({
                            id: income.billId,
                            invoiceNo: income.invoiceNo
                        });
                    }
                }
            }
        }).catch((error) => {
            const message = error.response?.data?.Error || 'Error loading income data';
            alert(message);
        });
    }, [incomeId, withLoading]);

    useEffect(() => {
        if (formData.bindToBill && formData.clientId) {
            fetchBills(formData.clientId, formData.projectId);
        } else {
            setBills([]);
        }
    }, [formData.bindToBill, formData.clientId, formData.projectId, fetchBills]);

    const filteredProjects = useMemo(() => {
        if (!formData.clientId || !formData.bindToBill) {
            return [];
        }
        return projects.filter(
            (project) => String(project.clientId) === String(formData.clientId)
        );
    }, [projects, formData.clientId, formData.bindToBill]);

    const filteredBills = useMemo(() => {
        const term = billSearchTerm.toLowerCase();
        return bills.filter((bill) =>
            (bill.invoiceNo || '').toLowerCase().includes(term) ||
            (bill.partyName || '').toLowerCase().includes(term) ||
            (bill.workTitle || '').toLowerCase().includes(term)
        );
    }, [bills, billSearchTerm]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const nextValue = type === 'checkbox' ? checked : value;

        setFormData((prev) => {
            const next = { ...prev, [name]: nextValue };

            if (name === 'bindToBill') {
                if (!checked) {
                    next.clientId = '';
                    next.projectId = '';
                    next.billId = '';
                    setSelectedBill(null);
                    setBills([]);
                }
            }

            if (name === 'clientId') {
                const projectStillValid = prev.projectId && projects.some(
                    (project) =>
                        String(project.id) === String(prev.projectId) &&
                        String(project.clientId) === String(value)
                );
                if (!projectStillValid) {
                    next.projectId = '';
                }
                next.billId = '';
                setSelectedBill(null);
            }

            if (name === 'projectId') {
                next.billId = '';
                setSelectedBill(null);
            }

            return next;
        });
    };

    const handleBillSelect = (bill) => {
        setSelectedBill(bill);
        setFormData((prev) => ({
            ...prev,
            billId: String(bill.id),
            projectId: bill.projectId ? String(bill.projectId) : prev.projectId
        }));
        setShowBillModal(false);
        setBillSearchTerm('');
    };

    const buildPayload = () => ({
        date: formData.date,
        amount: parseFloat(formData.amount),
        type: formData.type,
        description: formData.description.trim(),
        bindToBill: formData.bindToBill,
        clientId: formData.bindToBill && formData.clientId ? Number(formData.clientId) : null,
        projectId: formData.bindToBill && formData.projectId ? Number(formData.projectId) : null,
        billId: formData.bindToBill && formData.billId ? Number(formData.billId) : null
    });

    const handleSubmit = (e) => {
        e.preventDefault();

        if (formData.bindToBill && !formData.billId) {
            alert('Please select a bill to bind this income');
            return;
        }

        const payload = buildPayload();
        const apiCall = incomeId
            ? api.put(`/income/${incomeId}`, payload)
            : api.post('/income', payload);

        apiCall
            .then(() => {
                navigate('/financials/income');
            })
            .catch((error) => {
                const message = error.response?.data?.Error || 'Error saving income';
                alert(message);
            });
    };

    const handleDelete = () => {
        if (!window.confirm('Are you sure you want to delete this income entry?')) return;

        api.delete(`/income/${incomeId}`)
            .then(() => {
                navigate('/financials/income');
            })
            .catch((error) => {
                const message = error.response?.data?.Error || 'Error deleting income';
                alert(message);
            });
    };

    const bindingFieldsDisabled = !formData.bindToBill;

    return (
        <PageLoadingArea isLoading={isLoading}>
            <div className="form-page">
                <div className="form-page-header">
                    <div>
                        <h1>{incomeId ? 'Modify income' : 'Add income'}</h1>
                        <p className="form-page-subtitle">
                            {incomeId
                                ? 'Update income details'
                                : 'Record a new income entry'}
                        </p>
                    </div>
                    <div className="form-page-actions">
                        <button
                            type="button"
                            className="form-page-btn-secondary"
                            onClick={() => navigate('/financials/income')}
                        >
                            Back to list
                        </button>
                        {incomeId && (
                            <button
                                type="button"
                                className="form-page-btn-danger"
                                onClick={handleDelete}
                            >
                                Delete income
                            </button>
                        )}
                        <button
                            type="submit"
                            form="create-income-form"
                            className="form-page-btn"
                        >
                            {incomeId ? 'Update income' : 'Save income'}
                        </button>
                    </div>
                </div>

                <section className="form-page-section">
                    <div className="form-page-section-head">
                        <div>
                            <h2>Income details</h2>
                            <p className="form-page-section-hint">Date, amount, type, and description</p>
                        </div>
                    </div>

                    <form id="create-income-form" onSubmit={handleSubmit}>
                        <div className="form-page-grid cols-3">
                            <div className="form-page-field">
                                <label htmlFor="date">Date</label>
                                <DatePicker
                                    id="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="amount">Amount</label>
                                <input
                                    type="number"
                                    id="amount"
                                    name="amount"
                                    value={formData.amount}
                                    onChange={handleChange}
                                    required
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                />
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="type">Type</label>
                                <select
                                    id="type"
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="Cash">Cash</option>
                                    <option value="Cheque">Cheque</option>
                                    <option value="Online">Online</option>
                                </select>
                            </div>
                            <div className="form-page-field span-full">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="3"
                                    required
                                    placeholder="Enter income description"
                                />
                            </div>
                        </div>
                    </form>
                </section>

                <section className="form-page-section">
                    <div className="form-page-section-head">
                        <div>
                            <h2>Bill binding</h2>
                            <p className="form-page-section-hint">
                                Optionally link this income to an invoice and update amount received
                            </p>
                        </div>
                    </div>

                    <div className="form-page-grid cols-3">
                        <div className="form-page-field span-full">
                            <label className="form-page-checkbox-label">
                                <input
                                    type="checkbox"
                                    name="bindToBill"
                                    checked={formData.bindToBill}
                                    onChange={handleChange}
                                />
                                Bind income against a bill
                            </label>
                        </div>
                        <div className="form-page-field">
                            <label htmlFor="clientId">
                                Client{formData.bindToBill ? ' (required)' : ''}
                            </label>
                            <select
                                id="clientId"
                                name="clientId"
                                value={formData.clientId}
                                onChange={handleChange}
                                disabled={bindingFieldsDisabled}
                                required={formData.bindToBill}
                            >
                                <option value="">
                                    {formData.bindToBill ? 'Select client' : 'Enable bill binding first'}
                                </option>
                                {clients.map((client) => (
                                    <option key={client.id} value={client.id}>
                                        {client.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-page-field">
                            <label htmlFor="projectId">Project (optional)</label>
                            <select
                                id="projectId"
                                name="projectId"
                                value={formData.projectId}
                                onChange={handleChange}
                                disabled={bindingFieldsDisabled || !formData.clientId}
                            >
                                <option value="">
                                    {!formData.bindToBill
                                        ? 'Enable bill binding first'
                                        : formData.clientId
                                            ? 'All projects'
                                            : 'Select a client first'}
                                </option>
                                {filteredProjects.map((project) => (
                                    <option key={project.id} value={project.id}>
                                        {project.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-page-field">
                            <label>Bill{formData.bindToBill ? ' (required)' : ''}</label>
                            <div className="form-page-inline-actions">
                                <button
                                    type="button"
                                    className="form-page-btn-secondary"
                                    onClick={() => setShowBillModal(true)}
                                    disabled={bindingFieldsDisabled || !formData.clientId}
                                >
                                    {selectedBill ? 'Change bill' : 'Select bill'}
                                </button>
                            </div>
                            {selectedBill && (
                                <p className="form-page-field-hint">
                                    Selected: {selectedBill.invoiceNo}
                                    {selectedBill.partyName ? ` · ${selectedBill.partyName}` : ''}
                                    {selectedBill.totalAmount != null || selectedBill.amountIncGst != null
                                        ? ` · ${formatMoney(getBillAmountIncGst(selectedBill))}`
                                        : ''}
                                </p>
                            )}
                        </div>
                    </div>
                </section>

                {showBillModal && (
                    <div className="form-page-modal-overlay" onClick={() => setShowBillModal(false)}>
                        <div className="form-page-modal wide bill-picker" onClick={(e) => e.stopPropagation()}>
                            <div className="form-page-modal-head">
                                <div>
                                    <h2>Select bill</h2>
                                    <p>Choose an invoice for this income entry</p>
                                </div>
                                <button
                                    type="button"
                                    className="form-page-btn-ghost"
                                    onClick={() => setShowBillModal(false)}
                                >
                                    Close
                                </button>
                            </div>

                            <input
                                type="text"
                                placeholder="Search invoice no, party, or work title"
                                value={billSearchTerm}
                                onChange={(e) => setBillSearchTerm(e.target.value)}
                                className="form-page-search"
                                autoFocus
                            />

                            <div className="form-page-table-wrap">
                                <table className="form-page-table">
                                    <thead>
                                        <tr>
                                            <th>Invoice No</th>
                                            <th>Date</th>
                                            <th>Party</th>
                                            <th>Work title</th>
                                            <th className="col-num">Amount (incl. GST)</th>
                                            <th className="col-num col-received">Amount received</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredBills.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" style={{ textAlign: 'center', color: '#888' }}>
                                                    {formData.clientId
                                                        ? 'No bills found for the selected client/project'
                                                        : 'Select a client first'}
                                                </td>
                                            </tr>
                                        ) : filteredBills.map((bill) => (
                                            <tr
                                                key={bill.id}
                                                className="form-page-picker-row"
                                                onClick={() => handleBillSelect(bill)}
                                            >
                                                <td>{bill.invoiceNo}</td>
                                                <td>{formatDateDDMMYYYY(bill.invoiceDate)}</td>
                                                <td>{bill.partyName}</td>
                                                <td>{bill.workTitle || '—'}</td>
                                                <td className="col-num">{formatMoney(getBillAmountIncGst(bill))}</td>
                                                <td className="col-num col-received">{formatMoney(bill.amountReceived)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="form-page-modal-foot">
                                <button
                                    type="button"
                                    className="form-page-btn-secondary"
                                    onClick={() => setShowBillModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </PageLoadingArea>
    );
}

export default CreateIncome;
